import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export interface MessageComposeData {
  listingId: string;
  listingTableName: string;
  sellerId: string;
  sellerName: string;
  listingTitle: string;
  listingPrice?: string;
}

@Component({
  selector: 'app-message-compose-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="modal-overlay" (click)="onCancel()" *ngIf="show">
      <div class="modal-container" (click)="$event.stopPropagation()">
        <div class="modal-header">
          <h3>Send Message to {{ data?.sellerName || 'Seller' }}</h3>
          <button class="close-btn" (click)="onCancel()" type="button">
            ×
          </button>
        </div>

        <div class="modal-body">
          <div class="listing-info" *ngIf="data">
            <h4>{{ data.listingTitle }}</h4>
            <p class="listing-price" *ngIf="data.listingPrice">
              {{ data.listingPrice }}
            </p>
          </div>

          <div class="message-form">
            <label for="message-text">Your Message:</label>
            <textarea
              id="message-text"
              [(ngModel)]="messageText"
              placeholder="Hi! I'm interested in your listing..."
              class="message-textarea"
              rows="6"
              maxlength="1000"
              [disabled]="sending"
            ></textarea>

            <div class="character-count">
              <span
                [style.color]="messageText.length > 900 ? '#ef4444' : '#6b7280'"
              >
                {{ messageText.length }}/1000 characters
              </span>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            class="btn-cancel"
            (click)="onCancel()"
            type="button"
            [disabled]="sending"
          >
            Cancel
          </button>
          <button
            class="btn-send"
            (click)="onSend()"
            type="button"
            [disabled]="!canSend() || sending"
          >
            <span *ngIf="!sending">Send Message</span>
            <span *ngIf="sending">Sending...</span>
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.6);
        backdrop-filter: blur(4px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        animation: fadeIn 0.3s ease-out;
        padding: 20px;
      }

      .modal-container {
        background: white;
        border-radius: 16px;
        max-width: 500px;
        width: 100%;
        max-height: 90vh;
        overflow-y: auto;
        box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
        animation: slideUp 0.3s ease-out;
      }

      .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 24px 24px 0 24px;
        border-bottom: 1px solid #e5e7eb;
        margin-bottom: 20px;
      }

      .modal-header h3 {
        margin: 0;
        color: #111827;
        font-size: 1.25rem;
        font-weight: 600;
        line-height: 1.3;
      }

      .close-btn {
        background: none;
        border: none;
        font-size: 2rem;
        cursor: pointer;
        padding: 4px;
        color: #6b7280;
        border-radius: 8px;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        line-height: 1;
      }

      .close-btn:hover {
        color: #111827;
        background: #f3f4f6;
      }

      .modal-body {
        padding: 0 24px 20px 24px;
      }

      .listing-info {
        background: #f8fafc;
        padding: 16px;
        border-radius: 12px;
        margin-bottom: 20px;
        border-left: 4px solid #3b82f6;
      }

      .listing-info h4 {
        margin: 0 0 8px 0;
        color: #111827;
        font-size: 1rem;
        font-weight: 600;
      }

      .listing-price {
        margin: 0;
        color: #059669;
        font-weight: 600;
        font-size: 0.9rem;
      }

      .message-form label {
        display: block;
        margin-bottom: 8px;
        color: #374151;
        font-weight: 500;
        font-size: 0.9rem;
      }

      .message-textarea {
        width: 100%;
        padding: 12px;
        border: 2px solid #e5e7eb;
        border-radius: 12px;
        resize: vertical;
        font-size: 0.9rem;
        transition: all 0.3s ease;
        background: #ffffff;
        box-sizing: border-box;
        min-height: 120px;
      }

      .message-textarea:focus {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }

      .message-textarea:disabled {
        background: #f9fafb;
        opacity: 0.7;
        cursor: not-allowed;
      }

      .message-textarea::placeholder {
        color: #9ca3af;
        font-style: italic;
      }

      .character-count {
        text-align: right;
        margin-top: 8px;
        font-size: 0.8rem;
        font-weight: 500;
      }

      .modal-footer {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        padding: 20px 24px 24px 24px;
        border-top: 1px solid #e5e7eb;
      }

      .btn-cancel,
      .btn-send {
        padding: 12px 24px;
        border-radius: 8px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
        font-size: 0.9rem;
        border: 2px solid;
        min-width: 100px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .btn-cancel {
        background: #ffffff;
        color: #374151;
        border-color: #d1d5db;
      }

      .btn-cancel:hover:not(:disabled) {
        background: #f9fafb;
        border-color: #9ca3af;
      }

      .btn-send {
        background: #3b82f6;
        color: white;
        border-color: #3b82f6;
      }

      .btn-send:hover:not(:disabled) {
        background: #2563eb;
        border-color: #2563eb;
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
      }

      .btn-cancel:disabled,
      .btn-send:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
        box-shadow: none;
      }

      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }

      @keyframes slideUp {
        from {
          opacity: 0;
          transform: translateY(20px) scale(0.95);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }

      @media (max-width: 768px) {
        .modal-container {
          margin: 10px;
          max-width: calc(100% - 20px);
        }

        .modal-header,
        .modal-body,
        .modal-footer {
          padding-left: 16px;
          padding-right: 16px;
        }

        .modal-footer {
          flex-direction: column;
          gap: 8px;
        }

        .btn-cancel,
        .btn-send {
          width: 100%;
        }
      }
    `,
  ],
})
export class MessageComposeModalComponent implements OnInit {
  @Input() show = false;
  @Input() data: MessageComposeData | null = null;
  @Output() send = new EventEmitter<string>();
  @Output() cancel = new EventEmitter<void>();

  messageText = '';
  sending = false;

  ngOnInit() {
    if (this.show) {
      document.body.style.overflow = 'hidden';
    }
  }

  ngOnDestroy() {
    document.body.style.overflow = 'auto';
  }

  onSend() {
    if (this.canSend() && !this.sending) {
      this.sending = true;
      this.send.emit(this.messageText.trim());
    }
  }

  onCancel() {
    if (!this.sending) {
      this.cancel.emit();
      this.resetModal();
    }
  }

  canSend(): boolean {
    return (
      this.messageText.trim().length > 0 && this.messageText.length <= 1000
    );
  }

  resetModal() {
    this.messageText = '';
    this.sending = false;
    document.body.style.overflow = 'auto';
  }

  setSending(sending: boolean) {
    this.sending = sending;
  }
}
